// ---------------------------------------------------------------------------
// logger
//
// A thin, PHI-safe structured logging layer for post-hoc debugging in PostHog.
//
// WHY EVENTS, NOT "PostHog Logs": this is a browser SPA. The `posthog-js` SDK
// has no separate log-ingestion API, and PostHog's OTLP Logs product does not
// auto-attribute to a person profile. Capturing structured EVENTS instead means
// every log line is automatically tied to the signed-in provider's
// `distinct_id` (we already `analytics.identify()` on login), so you can filter
// the `app_log` event by person and `log_flow` to get a per-user, time-ordered
// debug trail of exactly what they did — "this user searched for these
// patients, generated this note, pushed this telephone encounter".
//
// HOW TO READ THEM IN POSTHOG:
//   - Every call below captures one `app_log` event.
//   - Filter on `log_flow` (e.g. `patient_search`, `note_generation`),
//     `log_level` (`debug|info|warn|error`), and `log_event` (the step).
//   - Scope to a single provider via the person / `distinct_id`.
//   - A good saved insight: events = `app_log`, breakdown by `log_event`,
//     filtered to `log_level = error` — your live error feed per user.
//
// PHI SAFETY (this is a healthcare app — read before adding logs):
//   - NEVER pass raw patient identifiers or clinical free text (names, DOB,
//     MRN, phone, transcripts, note bodies, dictation) as a value. Pass IDs
//     (`patient_id`, `encounter_id`), counts, lengths, durations, booleans,
//     and error names/messages instead.
//   - As defense-in-depth, `sanitize()` truncates long strings and redacts a
//     denylist of known-PHI key names, but the primary safeguard is the
//     discipline of logging metadata, not content.
// ---------------------------------------------------------------------------

import {analytics} from './analytics';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogContext = Record<string, unknown> | undefined;

// The single PostHog event name under which all structured logs are captured.
const LOG_EVENT = 'app_log';

// Canonical flow names. Use these so every call site bucket-matches in PostHog
// instead of drifting into `patientSearch` vs `patient_search` vs `search`.
export const LogFlow = {
  PatientSearch: 'patient_search',
  NoteGeneration: 'note_generation',
  NoteEdit: 'note_edit',
  Charting: 'charting',
  PatientChart: 'patient_chart',
  PhoneEncounter: 'phone_encounter',
  Encounter: 'encounter',
  Auth: 'auth',
  Ehr: 'ehr',
  Tools: 'tools',
} as const;

export type LogFlowName = (typeof LogFlow)[keyof typeof LogFlow] | string;

// Cap on any single string property so an accidental transcript / note body
// can never be dumped wholesale into analytics.
const MAX_STRING_LEN = 256;

// Exact / substring key names that are known to carry PHI in this codebase.
// Values under these keys are redacted (replaced with `[redacted:<len>]`) so a
// careless `{patientName}` shorthand can't leak through. Add to this list
// rather than relaxing it.
const PHI_KEY_PATTERN =
  /(^|_)(name|patient.*name|first.*name|last.*name|full.*name|display.*name|dob|birth|mrn|account.*no|ssn|phone|email|address|transcript|message|messages|note|content|text|query|q|search.*text|reason.*text|before|after|sections?)($|_)/i;

// Keys that look PHI-ish by the pattern above but are safe structural metadata
// we explicitly want to keep (e.g. `template_name`, `model`, `error_name`).
const PHI_KEY_ALLOWLIST =
  /^(template_name|model|error_name|provider_id|patient_id|ehr_patient_id|encounter_id|conversation_id|company_id|facility_id|section_name|section_count|reason_code|note_count|message_count)$/i;

const isProd = (() => {
  try {
    return process.env.NODE_ENV === 'production';
  } catch {
    return false;
  }
})();

function redactValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return `[redacted:${value.length}]`;
  }
  if (Array.isArray(value)) {
    return `[redacted:array:${value.length}]`;
  }
  return '[redacted]';
}

function clampString(value: string): string {
  if (value.length <= MAX_STRING_LEN) return value;
  return `${value.slice(0, MAX_STRING_LEN)}…(+${value.length - MAX_STRING_LEN})`;
}

// Defensive, shallow sanitize. We intentionally do NOT deep-walk arbitrary
// nested objects — callers should pass flat, structured metadata. Nested
// objects are JSON-stringified and clamped so we never recurse into a giant
// payload.
function sanitize(context: LogContext): Record<string, unknown> {
  if (!context) return {};
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(context)) {
    if (raw === undefined) continue;
    // A number/boolean/null can never be PHI — a `query_length: 5` or
    // `message_count: 12` is safe even though the key contains a PHI-ish word.
    // Only redact PHI keys carrying a string / array / object value (the shapes
    // that can actually hold a name, transcript, or note body).
    const valueCanHoldPhi =
      typeof raw !== 'number' && typeof raw !== 'boolean' && raw !== null;
    const isPhiKey =
      valueCanHoldPhi &&
      PHI_KEY_PATTERN.test(key) &&
      !PHI_KEY_ALLOWLIST.test(key);
    if (isPhiKey) {
      out[key] = redactValue(raw);
      continue;
    }
    if (typeof raw === 'string') {
      out[key] = clampString(raw);
    } else if (
      typeof raw === 'number' ||
      typeof raw === 'boolean' ||
      raw === null
    ) {
      out[key] = raw;
    } else {
      // Objects/arrays of structural metadata: stringify + clamp so a stray
      // large payload can't balloon the event.
      try {
        out[key] = clampString(JSON.stringify(raw));
      } catch {
        out[key] = '[unserializable]';
      }
    }
  }
  return out;
}

// Pull a safe, structured summary out of an unknown thrown value. Use this for
// the `error` field of `logger.error(...)` calls.
export function describeError(error: unknown): Record<string, unknown> {
  if (!error) return {error_name: 'unknown', error_message: null};
  if (error instanceof Error) {
    return {
      error_name: error.name || 'Error',
      error_message: clampString(error.message || ''),
    };
  }
  if (typeof error === 'object') {
    const anyErr = error as Record<string, unknown>;
    return {
      error_name: String(anyErr.name || anyErr.code || 'Error'),
      error_message: clampString(String(anyErr.message || '')),
    };
  }
  return {error_name: 'Error', error_message: clampString(String(error))};
}

function consoleMirror(
  level: LogLevel,
  flow: LogFlowName,
  event: string,
  props: Record<string, unknown>,
) {
  // Keep production consoles clean; PostHog is the post-hoc store there. In
  // development, mirror so the same structured line is visible while you work.
  if (isProd) return;
  const label = `[${flow}] ${event}`;
  const fn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.info;
  try {
    fn(label, props);
  } catch {
    /* console is best-effort */
  }
}

function emit(
  level: LogLevel,
  flow: LogFlowName,
  event: string,
  context?: LogContext,
) {
  const props = {
    log_level: level,
    log_flow: flow,
    log_event: event,
    log_ts: new Date().toISOString(),
    ...sanitize(context),
  };
  consoleMirror(level, flow, event, props);
  try {
    analytics.capture(LOG_EVENT, props);
  } catch {
    // Logging must never break a user flow.
  }
}

export interface ScopedLogger {
  flow: LogFlowName;
  debug(event: string, context?: LogContext): void;
  info(event: string, context?: LogContext): void;
  warn(event: string, context?: LogContext): void;
  error(event: string, context?: LogContext): void;
}

export const logger = {
  debug(flow: LogFlowName, event: string, context?: LogContext) {
    emit('debug', flow, event, context);
  },
  info(flow: LogFlowName, event: string, context?: LogContext) {
    emit('info', flow, event, context);
  },
  warn(flow: LogFlowName, event: string, context?: LogContext) {
    emit('warn', flow, event, context);
  },
  error(flow: LogFlowName, event: string, context?: LogContext) {
    emit('error', flow, event, context);
  },

  /**
   * Bind a flow so call sites read `log.info('step', {...})`. The single most
   * ergonomic way to instrument a service or hook.
   */
  scope(flow: LogFlowName): ScopedLogger {
    return {
      flow,
      debug: (event, context) => emit('debug', flow, event, context),
      info: (event, context) => emit('info', flow, event, context),
      warn: (event, context) => emit('warn', flow, event, context),
      error: (event, context) => emit('error', flow, event, context),
    };
  },
};

export default logger;
